<script setup lang="ts">
import maplibregl from 'maplibre-gl'
import { watch, inject, ref, onUnmounted, type Ref } from 'vue'

import { useTrackResolution, type Resolution } from '@/composables/useTrackResolution'
import { buildLineStringFromSegments, parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { optimizeFitBoundsCameraOptions } from '@/services/mapCameraOptimizationService'
import { getTrackTypeCategory, shouldHideAscentForTourListItem } from '@/services/tourTypeService'
import { TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT } from '@/services/trackResolutionCacheService'
import { isSmallScreen } from '@/services/uiService'
import { useLibraryStore } from '@/stores/libraryStore'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useUiStore } from '@/stores/uiStore'
import type { TourListItem } from '@/types/tour'
import { getLibraryToursToRender } from '@/utils/libraryMapVisibility'
import { findFirstSymbolLayerId } from '@/utils/mapLayerOrder'
import { escapeHtml } from '@/utils/trackHoverPopup'
import {
  buildTrackDirectionArrowLayer,
  buildTrackLineLayerIds,
  buildTrackLineLayers,
  buildTrackSurfaceDashLayer,
  TRACK_LINE_LAYOUT,
  TRACK_LINE_STYLE,
} from '@/utils/trackLineLayerFactory'
import {
  buildTrackProfileLegFeatureCollection,
  type TrackProfileAnalysis,
} from '@/utils/trackProfileAnalysis'

import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl'

type LibraryToursUploadedDetail = {
  tourIds?: string[]
}

const emit = defineEmits<{
  'contextmenu-tour': [
    detail: {
      tourId: string
      trackClientId: string
      trackIndex: number
      clientX: number
      clientY: number
    },
  ]
}>()

const props = defineProps<{
  metricColorizedTrack?: {
    tourId: string
    trackClientId: string
    analysis: TrackProfileAnalysis
    baseColor?: string
    surfacePattern?: boolean
  } | null
}>()

const libraryStore = useLibraryStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const uiStore = useUiStore()

const mapInstance = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
const mapStyleRevision = inject<Ref<number>>('mapStyleRevision', ref(0))

const isRouteModeActive = () => uiStore.isTrackDrawingActive || uiStore.isTrackEditMode

// Two fixed sources instead of one per tour
const MAIN_SOURCE_ID = 'tour-library-main'
const OVERLAY_SOURCE_ID = 'tour-library-overlay'
// Shared prefix so getVisibleEntityIds('tour-library-', ...) finds features in both sources
const LAYER_PREFIX = 'tour-library-'
const MOBILE_PANEL_COLLAPSED_HEIGHT = 72
const TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX = 12

// Fixed layer IDs (8 total, vs N×4 in the old model)
const MAIN_IDS = buildTrackLineLayerIds('tour-library-main', 'layer')
const OVERLAY_IDS = buildTrackLineLayerIds('tour-library-overlay', 'layer')
const MAIN_SURFACE_DASH_LAYER_ID = 'tour-library-main-surface-dash-layer'
const OVERLAY_SURFACE_DASH_LAYER_ID = 'tour-library-overlay-surface-dash-layer'

// Track resolution composable
const { targetResolution, getVisibleEntityIds, fetchTourGeometries, scheduleUpdate, clearCache } =
  useTrackResolution(mapInstance, mapLoaded)

// Track which resolution each tour is currently rendered at
const renderedResolution = new Map<string, Resolution>()
type OverrideResolution = Exclude<Resolution, '500m'>
type TrackCoordsById = Map<string, [number, number][]>
const renderedGeometryCache = new Map<
  string,
  Partial<Record<OverrideResolution, TrackCoordsById>>
>()
let pendingUploadedTourIds: string[] | null = null
let pendingUploadedZoomAttempts = 0
let pendingUploadedZoomTimer: ReturnType<typeof setTimeout> | null = null

const discardCachedGeometry = (tourId: string) => {
  renderedGeometryCache.delete(tourId)
  renderedResolution.delete(tourId)
}

const shouldPreferLocalGeometry = (tourId: string) =>
  libraryStore.locallyModifiedTourIds.has(tourId)

const isMetricColorizedTrack = (tourId: string, trackId?: string) =>
  trackId != null &&
  props.metricColorizedTrack?.tourId === tourId &&
  props.metricColorizedTrack?.trackClientId === trackId

const resolveSurfacePavedStatus = (categoryId: unknown) =>
  categoryId === 'paved' ? 'paved' : 'unpaved'

// Paint constants
const DEFAULT_OUTLINE_WIDTH = TRACK_LINE_STYLE.outlineWidth
const DEFAULT_OUTLINE_OPACITY = TRACK_LINE_STYLE.outlineOpacity
const DEFAULT_LINE_WIDTH = TRACK_LINE_STYLE.lineWidth
const DEFAULT_LINE_OPACITY = 1
const DEFAULT_ARROW_OPACITY = TRACK_LINE_STYLE.arrowOpacity
const HIGHLIGHT_OUTLINE_WIDTH = 10
const HIGHLIGHT_OUTLINE_OPACITY = 0.9
const HIGHLIGHT_LINE_WIDTH = 5
const DIMMED_LINE_OPACITY = 0.3
const DIMMED_OUTLINE_OPACITY = 0.2
const DIMMED_ARROW_OPACITY = 0.3

// ── Popup formatters ─────────────────────────────────────────────────────────

const fmtDistance = (m: number | null | undefined): string => {
  if (m == null) return '—'
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`
}

const fmtElevation = (m: number | null | undefined): string =>
  m == null ? '—' : `${Math.round(m)} m`

const fmtDuration = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null => {
  if (!startDate || !endDate) return null
  const ms = Date.parse(endDate) - Date.parse(startDate)
  if (ms <= 0) return null
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const fmtDate = (s: string | null | undefined): string => {
  if (!s) return '—'
  return new Date(s).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Colors
const SELECTED_COLOR = '#ea580c' // orange-600
const ACTIVITY_COLOR = '#059669' // emerald-600
const ROUTE_COLOR = '#374151' // gray-700

// In-memory state replacing per-tour source/layer tracking
const mainFeatureCache = new Map<string, GeoJSON.Feature[]>() // tourId → main features
const tourBounds = new Map<string, maplibregl.LngLatBounds>() // tourId → bounds for zoom
let layersInitialized = false

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

// ── Feature builders ─────────────────────────────────────────────────────────

const buildTourFeatures = (
  tour: TourListItem,
  id: string,
  overrideCoordsByTrack?: TrackCoordsById,
): GeoJSON.Feature[] => {
  const isActivity = !!tour.type || !!tour.startDate
  const trackColor = isActivity ? ACTIVITY_COLOR : ROUTE_COLOR

  return tour.tracks.flatMap((track, trackIdx) => {
    if (isMetricColorizedTrack(id, track.clientId)) {
      const metricTrack = props.metricColorizedTrack
      const isSurfacePattern =
        metricTrack?.surfacePattern === true && metricTrack.analysis.metricId === 'surface'
      const surfacePatternColor = metricTrack?.baseColor ?? track.color ?? trackColor
      const metricFeatures = buildTrackProfileLegFeatureCollection(metricTrack?.analysis.legs ?? [])
      if (metricFeatures.features.length > 0) {
        return metricFeatures.features.map((feature) => ({
          type: 'Feature' as const,
          properties: {
            ...feature.properties,
            tourId: id,
            trackClientId: track.clientId ?? '',
            trackIndex: trackIdx,
            tourName: tour.name,
            color: isSurfacePattern ? surfacePatternColor : feature.properties.color,
            surfacePavedStatus: isSurfacePattern
              ? resolveSurfacePavedStatus(feature.properties.categoryId)
              : null,
          },
          geometry: feature.geometry,
        }))
      }
    }

    const coords =
      overrideCoordsByTrack?.get(track.clientId ?? `idx-${trackIdx}`) ??
      (track.pointsLineReduced500m ? parseLineString(track.pointsLineReduced500m) : [])
    if (coords.length < 2) return []
    return [
      {
        type: 'Feature' as const,
        properties: {
          tourId: id,
          trackClientId: track.clientId ?? '',
          trackIndex: trackIdx,
          tourName: tour.name,
          color: trackColor,
        },
        geometry: { type: 'LineString' as const, coordinates: coords as number[][] },
      },
    ]
  })
}

const buildRenderableDetailTour = (): TourListItem | null => {
  const tour = libraryStore.detailTour
  if (!tour) return null

  return {
    clientId: tour.clientId,
    name: tour.name,
    description: tour.description ?? null,
    type: tour.type ?? null,
    routingProfile: tour.routingProfile ?? null,
    distance: tour.distance ?? null,
    elevationGain: tour.elevationGain ?? null,
    startDate: tour.createdAt ?? null,
    endDate: tour.updatedAt ?? null,
    createdAt: tour.createdAt ?? null,
    updatedAt: tour.updatedAt ?? null,
    trackTypes: tour.tracks.map((track) => track.type).filter((type): type is string => !!type),
    workspaces: [],
    tracks: tour.tracks.map((track) => ({
      clientId: track.clientId,
      color: track.color,
      pointsLineReduced500m:
        track.pointsLineReduced5m ?? buildLineStringFromSegments(track.segments ?? []),
    })),
  }
}

const getRenderableTours = (): TourListItem[] => {
  const detailTour = buildRenderableDetailTour()
  if (!detailTour) return libraryStore.tours

  let replaced = false
  const tours = libraryStore.tours.map((tour) => {
    if (tour.clientId !== detailTour.clientId) return tour
    replaced = true
    return detailTour
  })

  return replaced ? tours : [...tours, detailTour]
}

const getRenderableTourById = (tourId: string) =>
  getRenderableTours().find((tour) => tour.clientId === tourId)

const buildMainFC = (): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: uiStore.areMapElementsForceHidden ? [] : Array.from(mainFeatureCache.values()).flat(),
})

const buildOverlayFC = (): GeoJSON.FeatureCollection => {
  if (uiStore.areMapElementsForceHidden) return EMPTY_FC

  // Route drawing/editing owns the map interaction, so background tracks remain dim instead of
  // gaining a hover highlight. Selected tracks still render in the overlay at full brightness.
  const hoveredId = isRouteModeActive() ? null : libraryStore.hoveredTourId
  const selectedIds = libraryStore.selectedTourIds
  const pinnedIds = libraryStore.pinnedTourIds
  const visibleIdSet = new Set(getToursToRender().map((t) => t.clientId))

  const features: GeoJSON.Feature[] = []

  // Selected (non-hovered, non-pinned) — rendered lowest in the overlay
  for (const tourId of selectedIds) {
    if (tourId === hoveredId) continue
    if (pinnedIds.has(tourId)) continue
    if (!visibleIdSet.has(tourId)) continue
    for (const f of mainFeatureCache.get(tourId) ?? []) {
      features.push({
        ...f,
        properties: { ...f.properties, color: SELECTED_COLOR, role: 'selected' },
      })
    }
  }

  // Pinned (drawn) tours that are selected — rendered above regular selected
  for (const tourId of pinnedIds) {
    if (tourId === hoveredId) continue
    if (!selectedIds.has(tourId)) continue
    if (!visibleIdSet.has(tourId)) continue
    for (const f of mainFeatureCache.get(tourId) ?? []) {
      features.push({
        ...f,
        properties: { ...f.properties, color: SELECTED_COLOR, role: 'selected' },
      })
    }
  }

  // Hovered — rendered last so it sits on top; keeps natural color if not also selected
  if (hoveredId && visibleIdSet.has(hoveredId)) {
    const isSelected = selectedIds.has(hoveredId)
    for (const f of mainFeatureCache.get(hoveredId) ?? []) {
      features.push({
        ...f,
        properties: {
          ...f.properties,
          color: isSelected ? SELECTED_COLOR : (f.properties?.color as string),
          role: 'hovered',
        },
      })
    }
  }

  return { type: 'FeatureCollection', features }
}

const computeBoundsFromFeatures = (features: GeoJSON.Feature[]): maplibregl.LngLatBounds => {
  const bounds = new maplibregl.LngLatBounds()
  for (const feature of features) {
    const geom = feature.geometry as GeoJSON.LineString
    for (const coord of geom.coordinates) {
      bounds.extend(coord as [number, number])
    }
  }
  return bounds
}

// ── Tour list helpers ─────────────────────────────────────────────────────────

const getToursToRender = () =>
  getLibraryToursToRender({
    tours: getRenderableTours(),
    showOnlyUploadedBatch: libraryStore.showOnlyUploadedBatch,
    uploadedBatchIds: libraryStore.uploadedBatchIds,
    isTrackDrawingMode: uiStore.isTrackDrawingActive,
    isTrackEditMode: uiStore.isTrackEditMode,
    selectedTourIds: libraryStore.selectedTourIds,
    editingTrackTourId: uiStore.editingTrackTourId,
    editingTrackClientId: uiStore.editingTrackClientId,
    showAllTours: uiStore.isAllActivitiesView,
    clientSideFilteredIds: libraryStore.clientSideFilteredIds,
    pinnedTourIds: libraryStore.pinnedTourIds,
  })

const buildOverrideCoordsByTrack = (
  tour: TourListItem,
  coords: [number, number][][],
): TrackCoordsById => {
  const byTrack = new Map<string, [number, number][]>()
  coords.forEach((trackCoords, trackIdx) => {
    if (trackCoords.length < 2) return
    byTrack.set(tour.tracks[trackIdx]?.clientId ?? `idx-${trackIdx}`, trackCoords)
  })
  return byTrack
}

// ── Main cache sync ───────────────────────────────────────────────────────────

const syncMainCache = () => {
  const currentTours = getRenderableTours()
  const currentTourIds = new Set(currentTours.map((t) => t.clientId))

  // Remove stale entries
  for (const tourId of mainFeatureCache.keys()) {
    if (!currentTourIds.has(tourId)) {
      mainFeatureCache.delete(tourId)
      tourBounds.delete(tourId)
      discardCachedGeometry(tourId)
    }
  }

  // Rebuild all entries — preserves cached high-res coords where applicable
  for (const tour of currentTours) {
    const id = tour.clientId
    if (!id || id === 'undefined') continue
    if (shouldPreferLocalGeometry(id)) discardCachedGeometry(id)

    const currentResolution = renderedResolution.get(id)
    const cachedCoords =
      !shouldPreferLocalGeometry(id) && currentResolution && currentResolution !== '500m'
        ? renderedGeometryCache.get(id)?.[currentResolution]
        : undefined

    const features = buildTourFeatures(tour, id, cachedCoords)
    mainFeatureCache.set(id, features)
    if (features.length > 0) {
      tourBounds.set(id, computeBoundsFromFeatures(features))
    } else {
      tourBounds.delete(id)
    }
  }
}

// ── Source refresh helpers ────────────────────────────────────────────────────

const refreshMainSource = () => {
  const source = mapInstance.value?.getSource(MAIN_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildMainFC())
}

const refreshOverlaySource = () => {
  const source = mapInstance.value?.getSource(OVERLAY_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(buildOverlayFC())
}

const applyMainFilter = () => {
  const map = mapInstance.value
  if (!map || !layersInitialized) return

  const visibleIds = getToursToRender()
    .map((t) => (t.clientId && t.clientId !== 'undefined' ? t.clientId : null))
    .filter((id): id is string => id !== null)

  const filter = [
    'in',
    ['get', 'tourId'],
    ['literal', visibleIds],
  ] as maplibregl.FilterSpecification

  for (const layerId of [
    MAIN_IDS.touchTargetId,
    MAIN_IDS.outlineId,
    MAIN_IDS.lineId,
    MAIN_IDS.arrowId,
  ]) {
    if (map.getLayer(layerId)) map.setFilter(layerId, filter)
  }

  // The surface-dash layer must keep its intrinsic "unpaved only" filter — compose it with the
  // tour-visibility filter rather than replacing it. Overwriting with the bare visibility filter
  // made the dashes paint over EVERY library track (the overview looked dashed instead of solid);
  // the unpaved clause keeps the overview solid while still showing the surface pattern on a
  // selected track whose legs are tagged surfacePavedStatus='unpaved'.
  if (map.getLayer(MAIN_SURFACE_DASH_LAYER_ID)) {
    map.setFilter(MAIN_SURFACE_DASH_LAYER_ID, [
      'all',
      filter,
      ['==', ['get', 'surfacePavedStatus'], 'unpaved'],
    ] as maplibregl.FilterSpecification)
  }
}

// ── Emphasis dim (globally dims main source; overlay shows bright on top) ────

const applyMainSourceDim = (dimmed: boolean) => {
  const map = mapInstance.value
  if (!map || !layersInitialized) return

  if (!dimmed) {
    map.setPaintProperty(MAIN_IDS.outlineId, 'line-width', DEFAULT_OUTLINE_WIDTH)
    map.setPaintProperty(MAIN_IDS.outlineId, 'line-opacity', DEFAULT_OUTLINE_OPACITY)
    map.setPaintProperty(MAIN_IDS.lineId, 'line-width', DEFAULT_LINE_WIDTH)
    map.setPaintProperty(MAIN_IDS.lineId, 'line-opacity', DEFAULT_LINE_OPACITY)
    map.setPaintProperty(MAIN_IDS.arrowId, 'icon-opacity', DEFAULT_ARROW_OPACITY)
  } else {
    // Globally dim all main-source lines; overlay shows selected + hovered at full brightness
    map.setPaintProperty(MAIN_IDS.outlineId, 'line-opacity', DIMMED_OUTLINE_OPACITY)
    map.setPaintProperty(MAIN_IDS.lineId, 'line-opacity', DIMMED_LINE_OPACITY)
    map.setPaintProperty(MAIN_IDS.arrowId, 'icon-opacity', DIMMED_ARROW_OPACITY)
  }
}

// ── Hover popup ───────────────────────────────────────────────────────────────

const hoverPopup = ref<maplibregl.Popup | null>(null)
const isTouchEnvironment =
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)

// Returns an inline SVG string matching the Lucide icon for the tour's activity type.
// Used in raw HTML popup content where Vue components cannot be rendered.
const getTourTypeIconSvg = (tour: TourListItem): string => {
  const SVG_ATTRS =
    'xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"'

  if (!tour.type) {
    if (tour.startDate) {
      // Activity icon
      return `<svg ${SVG_ATTRS}><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/></svg>`
    }
    // Route icon
    return `<svg ${SVG_ATTRS}><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>`
  }

  const category = getTrackTypeCategory(tour.type)
  switch (category) {
    case 'aviation':
      return `<svg ${SVG_ATTRS}><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/></svg>`
    case 'cycling':
      return `<svg ${SVG_ATTRS}><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>`
    case 'driving':
      return `<svg ${SVG_ATTRS}><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18.4 6c-.3-.6-.9-1-1.6-1H7.2c-.7 0-1.3.4-1.6 1l-2.1 5.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>`
    case 'rail':
      return `<svg ${SVG_ATTRS}><path d="M4 15 2 22"/><path d="M20 15 22 22"/><path d="M4 9h16"/><path d="M12 3v6"/><rect x="4" y="3" width="16" height="12" rx="2"/><path d="M8 15h8"/><path d="M8 19h8"/></svg>`
    case 'hiking':
    case 'running':
    case 'walking':
      return `<svg ${SVG_ATTRS}><path d="M4 16v-2.38C4 11.5 2.97 10.5 3 8c.03-2.72 1.49-6 4.5-6C9.37 2 10 3.8 10 5.5c0 3.11-2 5.66-2 8.68V16a2 2 0 1 1-4 0Z"/><path d="M20 20v-2.38c0-2.12 1.03-3.12 1-5.62-.03-2.72-1.49-6-4.5-6C14.63 6 14 7.8 14 9.5c0 3.11 2 5.66 2 8.68V20a2 2 0 1 0 4 0Z"/><path d="M16 17h4"/><path d="M4 13h4"/></svg>`
    case 'swimming':
    case 'diving':
      return `<svg ${SVG_ATTRS}><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></svg>`
    case 'water_sports':
      return `<svg ${SVG_ATTRS}><path d="M22 18H2a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4Z"/><path d="M21 14 10 2 3 14h18Z"/><path d="M10 2v16"/></svg>`
    case 'gym_fitness_equipment':
      return `<svg ${SVG_ATTRS}><path d="M14.4 14.4 9.6 9.6"/><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z"/><path d="m21.5 21.5-1.4-1.4"/><path d="M3.9 3.9 2.5 2.5"/><path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z"/></svg>`
    default:
      return `<svg ${SVG_ATTRS}><path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/></svg>`
  }
}

const showTourPopup = (lngLat: maplibregl.LngLat, tour: TourListItem) => {
  const map = mapInstance.value
  if (!map) return
  if (hoverPopup.value) hoverPopup.value.remove()

  const iconSvg = getTourTypeIconSvg(tour)
  const date = fmtDate(tour.startDate ?? tour.createdAt)
  const distance = fmtDistance(tour.distance)
  const elevation = shouldHideAscentForTourListItem(tour) ? null : fmtElevation(tour.elevationGain)
  const duration = fmtDuration(tour.startDate, tour.endDate)
  const statsLine = [distance, elevation ? `↑ ${elevation}` : '']
    .filter(Boolean)
    .join(' &nbsp;·&nbsp; ')
  const pillsHtml = tour.workspaces
    .map(
      (w) =>
        `<span style="display:inline-block;background:#ecfdf5;color:#15803d;border:1px solid #a7f3d0;border-radius:4px;padding:1px 6px;font-size:10px;font-weight:500;white-space:nowrap;max-width:160px;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(w.name)}</span>`,
    )
    .join('')

  const html = `
    <div style="min-width:160px;max-width:220px;font-family:inherit;">
      <div style="display:flex;align-items:center;gap:5px;margin-bottom:4px;">
        <span style="flex-shrink:0;color:#374151;display:flex;align-items:center;">${iconSvg}</span>
        <span style="font-weight:600;font-size:13px;color:#111827;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(tour.name ?? '')}</span>
      </div>
      <div style="font-size:11px;color:#6b7280;margin-bottom:${pillsHtml ? '6px' : '0'};">
        <div>${date}${duration ? ` &nbsp;·&nbsp; ${duration}` : ''}</div>
        <div>${statsLine}</div>
      </div>
      ${pillsHtml ? `<div style="display:flex;flex-wrap:wrap;gap:4px;">${pillsHtml}</div>` : ''}
    </div>`

  hoverPopup.value = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 15,
    className: 'tour-library-hover-popup',
  })
    .setLngLat(lngLat)
    .setHTML(html)
    .addTo(map)
}

const updatePopupPosition = (lngLat: maplibregl.LngLat) => {
  hoverPopup.value?.setLngLat(lngLat)
}

const hideTourPopup = () => {
  hoverPopup.value?.remove()
  hoverPopup.value = null
}

// ── Layer event handlers (single set, shared across both touch-target layers) ─

type LayerHandlers = {
  click: (e: maplibregl.MapLayerMouseEvent) => void
  mouseenter: (e: maplibregl.MapLayerMouseEvent) => void
  mouseleave: () => void
  mousemove: (e: maplibregl.MapLayerMouseEvent) => void
  touchstart: (e: maplibregl.MapLayerTouchEvent) => void
  touchend: () => void
  contextmenu: (e: maplibregl.MapLayerMouseEvent) => void
}
const registeredLayerHandlers = new Map<string, LayerHandlers>()

// Long-press state (mobile only)
let longPressTimer: ReturnType<typeof setTimeout> | null = null
let longPressActivated = false
let longPressStartPoint: { x: number; y: number } | null = null

const cancelLongPress = () => {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
  longPressStartPoint = null
}

const isWithinLongPressMoveTolerance = (point: { x: number; y: number } | null) => {
  if (!longPressStartPoint || !point) return true
  const dx = point.x - longPressStartPoint.x
  const dy = point.y - longPressStartPoint.y
  return (
    dx * dx + dy * dy <= TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX * TOUCH_LONG_PRESS_MOVE_TOLERANCE_PX
  )
}

const touchMoveLongPressHandler = (event: maplibregl.MapTouchEvent) => {
  if (isWithinLongPressMoveTolerance(event.point)) return
  cancelLongPress()
}

const setupEventHandlers = (map: MapLibreMap) => {
  const touchLayerIds = [MAIN_IDS.touchTargetId]

  const getTrackFeatureFromEvent = (
    e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    const directFeature = e.features?.[0]
    if (directFeature) return directFeature
    if (!e.point || !map.getLayer(MAIN_IDS.touchTargetId)) return null
    return map.queryRenderedFeatures(e.point, { layers: [MAIN_IDS.touchTargetId] })[0] ?? null
  }

  // All tours live in the same layer, so moving between adjacent tracks only
  // fires mousemove (never mouseenter/mouseleave). Hover state must be driven
  // by mousemove, comparing the current feature's tourId to the stored value.
  const applyHoverFromEvent = (e: maplibregl.MapLayerMouseEvent) => {
    if (isTouchEnvironment) return
    const feature = getTrackFeatureFromEvent(e)
    const tourId = (feature?.properties?.tourId as string) ?? ''
    if (!tourId) return
    if (tourId !== libraryStore.hoveredTourId) {
      libraryStore.setHoveredTour(tourId)
      const tour = getRenderableTourById(tourId)
      if (tour) showTourPopup(e.lngLat, tour)
    } else {
      updatePopupPosition(e.lngLat)
    }
  }

  const touchStartHandler = (e: maplibregl.MapLayerTouchEvent) => {
    if (uiStore.isTrackDrawingActive) return
    if (uiStore.isTrackEditMode) return
    const feature = getTrackFeatureFromEvent(e)
    const tourId = feature?.properties?.tourId as string | undefined
    if (!tourId) return
    longPressActivated = false
    cancelLongPress()
    longPressStartPoint = e.point
    longPressTimer = setTimeout(() => {
      longPressTimer = null
      longPressActivated = true
      emitContextMenuFromEvent(e)
    }, 500)
  }

  const touchEndHandler = () => {
    cancelLongPress()
  }

  const clickHandler = (e: maplibregl.MapLayerMouseEvent) => {
    if (uiStore.isTrackDrawingActive) return
    if (uiStore.isTrackEditMode) return
    const feature = getTrackFeatureFromEvent(e)
    const clickedTourId = feature?.properties?.tourId as string | undefined
    if (!clickedTourId) return

    if (mobileBatchSelectionStore.activeSurface === 'library') {
      mobileBatchSelectionStore.toggle('tours', clickedTourId)
      libraryStore.toggleSelectedTour(clickedTourId)
      libraryStore.setScrollTarget(clickedTourId)
      return
    }

    if (isTouchEnvironment) {
      // Swallow click that followed a long-press (finger lift fires click too)
      if (longPressActivated) {
        longPressActivated = false
        return
      }
      if (libraryStore.isSelectionMode) {
        libraryStore.toggleSelectedTour(clickedTourId)
        libraryStore.setScrollTarget(clickedTourId)
      } else {
        libraryStore.setSelectedTourIds([clickedTourId])
        const tour = libraryStore.tours.find((t) => t.clientId === clickedTourId)
        if (tour) libraryStore.selectTourForDetail(tour, { focusMap: false })
        libraryStore.setScrollTarget(clickedTourId)
      }
    } else {
      const isMultiSelect = e.originalEvent.metaKey || e.originalEvent.ctrlKey
      if (isMultiSelect) {
        libraryStore.toggleSelectedTour(clickedTourId)
        libraryStore.closeDetail()
      } else {
        const isOnlySelected =
          libraryStore.selectedTourIds.size === 1 && libraryStore.selectedTourIds.has(clickedTourId)
        if (isOnlySelected) {
          libraryStore.clearSelectedTours()
          libraryStore.closeDetail()
        } else {
          libraryStore.setSelectedTourIds([clickedTourId])
          const tour = libraryStore.tours.find((t) => t.clientId === clickedTourId)
          if (tour) libraryStore.selectTourForDetail(tour, { focusMap: false })
        }
      }
      libraryStore.setScrollTarget(clickedTourId)
    }
  }

  const mouseEnterHandler = (e: maplibregl.MapLayerMouseEvent) => {
    if (isRouteModeActive()) return
    map.getCanvas().style.cursor = 'pointer'
    applyHoverFromEvent(e)
  }

  const mouseMoveHandler = (e: maplibregl.MapLayerMouseEvent) => {
    if (isRouteModeActive()) return
    applyHoverFromEvent(e)
  }

  const mouseLeaveHandler = () => {
    if (isRouteModeActive()) return
    map.getCanvas().style.cursor = ''
    libraryStore.clearHoveredTour()
    if (!isTouchEnvironment) hideTourPopup()
  }

  const contextMenuHandler = (e: maplibregl.MapLayerMouseEvent) => {
    if (uiStore.isTrackDrawingActive) return
    if (uiStore.isTrackEditMode) return
    e.preventDefault()
    emitContextMenuFromEvent(e)
  }

  const emitContextMenuFromEvent = (
    e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent,
  ) => {
    const feature = getTrackFeatureFromEvent(e)
    const tourId = feature?.properties?.tourId as string | undefined
    const trackClientId = feature?.properties?.trackClientId as string | undefined
    const trackIndex = Number(feature?.properties?.trackIndex ?? 0)
    if (!tourId || !trackClientId) return

    libraryStore.setHoveredTour(tourId)

    const originalEvent = e.originalEvent as MouseEvent | TouchEvent | undefined
    if (!originalEvent) return

    let clientX: number | null = null
    let clientY: number | null = null
    if ('clientX' in originalEvent && typeof originalEvent.clientX === 'number') {
      clientX = originalEvent.clientX
      clientY = originalEvent.clientY
    } else if ('changedTouches' in originalEvent) {
      const touch = originalEvent.changedTouches?.[0] ?? null
      clientX = touch?.clientX ?? null
      clientY = touch?.clientY ?? null
    }

    if (clientX === null || clientY === null) return
    emit('contextmenu-tour', { tourId, trackClientId, trackIndex, clientX, clientY })
  }

  // Cancel long-press only once movement exceeds normal finger jitter.
  map.on('touchmove', touchMoveLongPressHandler)

  for (const layerId of touchLayerIds) {
    if (!map.getLayer(layerId)) continue
    const handlers: LayerHandlers = {
      click: clickHandler,
      mouseenter: mouseEnterHandler,
      mouseleave: mouseLeaveHandler,
      mousemove: mouseMoveHandler,
      touchstart: touchStartHandler,
      touchend: touchEndHandler,
      contextmenu: contextMenuHandler,
    }
    registeredLayerHandlers.set(layerId, handlers)
    map.on('click', layerId, clickHandler)
    map.on('mouseenter', layerId, mouseEnterHandler)
    map.on('mouseleave', layerId, mouseLeaveHandler)
    map.on('mousemove', layerId, mouseMoveHandler)
    map.on('touchstart', layerId, touchStartHandler)
    map.on('touchend', layerId, touchEndHandler)
    map.on('contextmenu', layerId, contextMenuHandler)
  }
}

const removeEventHandlers = (map: MapLibreMap) => {
  map.off('touchmove', touchMoveLongPressHandler)
  cancelLongPress()
  for (const [layerId, h] of registeredLayerHandlers) {
    try {
      map.off('click', layerId, h.click)
      map.off('mouseenter', layerId, h.mouseenter)
      map.off('mouseleave', layerId, h.mouseleave)
      map.off('mousemove', layerId, h.mousemove)
      map.off('touchstart', layerId, h.touchstart)
      map.off('touchend', layerId, h.touchend)
      map.off('contextmenu', layerId, h.contextmenu)
    } catch {
      // Layer may already be removed
    }
  }
  registeredLayerHandlers.clear()
}

// ── Layer lifecycle ───────────────────────────────────────────────────────────

const initializeLayers = () => {
  const map = mapInstance.value
  if (!map) return
  const firstSymbolLayerId = findFirstSymbolLayerId(map)

  // Main source — all tours, natural colors
  map.addSource(MAIN_SOURCE_ID, { type: 'geojson', data: buildMainFC() })
  const mainLayers = buildTrackLineLayers({
    sourceId: MAIN_SOURCE_ID,
    ids: MAIN_IDS,
    lineColor: ['get', 'color'],
  })
  mainLayers.orderedLayers.forEach((layer) => map.addLayer(layer, firstSymbolLayerId))
  map.addLayer(
    buildTrackSurfaceDashLayer({
      id: MAIN_SURFACE_DASH_LAYER_ID,
      sourceId: MAIN_SOURCE_ID,
    }),
    MAIN_IDS.arrowId,
  )

  // Overlay source — selected + hovered tours, data-driven widths for hover highlight
  map.addSource(OVERLAY_SOURCE_ID, { type: 'geojson', data: EMPTY_FC })

  const hoverOutlineWidth = [
    'case',
    ['==', ['get', 'role'], 'hovered'],
    HIGHLIGHT_OUTLINE_WIDTH,
    DEFAULT_OUTLINE_WIDTH,
  ] as unknown as number

  const hoverLineWidth = [
    'case',
    ['==', ['get', 'role'], 'hovered'],
    HIGHLIGHT_LINE_WIDTH,
    DEFAULT_LINE_WIDTH,
  ] as unknown as number

  map.addLayer(
    {
      id: OVERLAY_IDS.outlineId,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { ...TRACK_LINE_LAYOUT },
      paint: {
        'line-color': '#ffffff',
        'line-width': hoverOutlineWidth,
        'line-opacity': HIGHLIGHT_OUTLINE_OPACITY,
      },
    },
    firstSymbolLayerId,
  )
  map.addLayer(
    {
      id: OVERLAY_IDS.lineId,
      type: 'line',
      source: OVERLAY_SOURCE_ID,
      layout: { ...TRACK_LINE_LAYOUT },
      paint: {
        'line-color': ['get', 'color'] as unknown as string,
        'line-width': hoverLineWidth,
      },
    },
    firstSymbolLayerId,
  )
  map.addLayer(
    buildTrackSurfaceDashLayer({
      id: OVERLAY_SURFACE_DASH_LAYER_ID,
      sourceId: OVERLAY_SOURCE_ID,
    }),
    firstSymbolLayerId,
  )
  map.addLayer(
    buildTrackDirectionArrowLayer({
      id: OVERLAY_IDS.arrowId,
      sourceId: OVERLAY_SOURCE_ID,
    }),
    firstSymbolLayerId,
  )

  setupEventHandlers(map)
  layersInitialized = true
  applyMainFilter()
  applyMainSourceDim(
    isRouteModeActive() || (!libraryStore.isSelectionMode && libraryStore.hoveredTourId !== null),
  )
}

const removeLayers = () => {
  const map = mapInstance.value
  if (!map) return

  removeEventHandlers(map)

  const allLayerIds = [
    MAIN_IDS.touchTargetId,
    MAIN_IDS.outlineId,
    MAIN_IDS.lineId,
    MAIN_SURFACE_DASH_LAYER_ID,
    MAIN_IDS.arrowId,
    OVERLAY_IDS.outlineId,
    OVERLAY_IDS.lineId,
    OVERLAY_SURFACE_DASH_LAYER_ID,
    OVERLAY_IDS.arrowId,
  ]
  for (const layerId of allLayerIds) {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing tour library layer', { layerId, error })
    }
  }
  for (const sourceId of [MAIN_SOURCE_ID, OVERLAY_SOURCE_ID]) {
    try {
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Error removing tour library source', {
        sourceId,
        error,
      })
    }
  }
  layersInitialized = false
}

// ── Viewport padding + zoom ───────────────────────────────────────────────────

const calculateExplorePadding = () => {
  const OFFSET = 30
  const padding = { top: OFFSET, right: OFFSET, bottom: OFFSET, left: OFFSET }

  const headerEl = document.querySelector('header') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 48
  padding.top = headerHeight + OFFSET

  if (uiStore.toolbarSidebarWidth > 0) {
    padding.left = uiStore.toolbarSidebarWidth + OFFSET
  }

  if (isSmallScreen.value) {
    const panelHeight = uiStore.isMobileBottomPanelExpanded
      ? Math.max(
          uiStore.libraryPanelHeight ?? MOBILE_PANEL_COLLAPSED_HEIGHT,
          MOBILE_PANEL_COLLAPSED_HEIGHT,
        )
      : MOBILE_PANEL_COLLAPSED_HEIGHT
    padding.bottom = panelHeight + 16
  }

  return padding
}

const optimizedFitBounds = (
  map: maplibregl.Map,
  bounds: maplibregl.LngLatBounds,
  options: maplibregl.FitBoundsOptions,
) => {
  map.fitBounds(bounds, optimizeFitBoundsCameraOptions(map, bounds, options))
}

const zoomToAllTours = (options: { explicit?: boolean } = {}) => {
  const map = mapInstance.value
  if (!map) return
  if (!uiStore.autoViewportAdaptationEnabled) return

  const visibleIds = new Set(
    getToursToRender()
      .map((t) => t.clientId)
      .filter(Boolean),
  )
  if (visibleIds.size === 0) return

  const allBounds = new maplibregl.LngLatBounds()
  for (const [tourId, bounds] of tourBounds) {
    if (visibleIds.has(tourId)) allBounds.extend(bounds)
  }

  if (!allBounds.isEmpty()) {
    optimizedFitBounds(map, allBounds, {
      padding: calculateExplorePadding(),
      maxZoom: 14,
      duration: 500,
      animate: options.explicit ? true : undefined,
    })
  }
}

const zoomToTourIds = (tourIds: string[]) => {
  const map = mapInstance.value
  if (!map) return false
  if (!uiStore.autoViewportAdaptationEnabled) return false

  const allBounds = new maplibregl.LngLatBounds()
  const missingIds: string[] = []

  for (const tourId of tourIds) {
    const bounds = tourBounds.get(tourId)
    if (!bounds || bounds.isEmpty()) {
      missingIds.push(tourId)
      continue
    }

    allBounds.extend(bounds)
  }

  if (missingIds.length > 0 || allBounds.isEmpty()) return false

  optimizedFitBounds(map, allBounds, {
    padding: calculateExplorePadding(),
    maxZoom: 14,
    duration: 500,
  })
  return true
}

const scheduleUploadedTourZoom = () => {
  if (!pendingUploadedTourIds) return
  if (pendingUploadedZoomTimer) return

  pendingUploadedZoomTimer = setTimeout(() => {
    pendingUploadedZoomTimer = null
    if (!pendingUploadedTourIds) return

    if (mapLoaded.value) {
      syncMainCache()
      refreshMainSource()
      refreshOverlaySource()
      applyMainFilter()
    }

    if (zoomToTourIds(pendingUploadedTourIds)) {
      pendingUploadedTourIds = null
      pendingUploadedZoomAttempts = 0
      return
    }

    pendingUploadedZoomAttempts++
    if (pendingUploadedZoomAttempts >= 30) {
      pendingUploadedTourIds = null
      pendingUploadedZoomAttempts = 0
      zoomToAllTours()
      return
    }

    scheduleUploadedTourZoom()
  }, 100)
}

const requestUploadedTourZoom = (tourIds: string[]) => {
  if (tourIds.length === 0) {
    setTimeout(zoomToAllTours, 100)
    return
  }

  pendingUploadedTourIds = Array.from(new Set(tourIds))
  pendingUploadedZoomAttempts = 0
  if (pendingUploadedZoomTimer) {
    clearTimeout(pendingUploadedZoomTimer)
    pendingUploadedZoomTimer = null
  }
  scheduleUploadedTourZoom()
}

// ── Watchers ──────────────────────────────────────────────────────────────────

let hasZoomedOnLoad = false
let previousTourIdSet = ''

// Main watcher: tour data, visibility conditions, filter changes
watch(
  [
    mapLoaded,
    () => libraryStore.tours,
    () => libraryStore.detailTour,
    () => libraryStore.showOnlyUploadedBatch,
    () => uiStore.isTrackDrawingActive,
    () => uiStore.isTrackEditMode,
    () => uiStore.editingTrackTourId,
    () => uiStore.editingTrackClientId,
    () => uiStore.isAllActivitiesView,
    () => libraryStore.clientSideFilteredIds,
  ],
  ([loaded]) => {
    if (!loaded) return

    syncMainCache()

    if (!layersInitialized) {
      initializeLayers()
    } else {
      refreshMainSource()
      applyMainFilter()
    }
    refreshOverlaySource()

    if (targetResolution.value !== '500m') {
      queueMicrotask(() => {
        scheduleUpdate(updateTourSourceResolution)
      })
    }

    const currentIdSet = getRenderableTours()
      .map((t) => t.clientId)
      .join(',')
    const tourSetChanged = currentIdSet !== previousTourIdSet
    previousTourIdSet = currentIdSet

    if (!hasZoomedOnLoad && getRenderableTours().length > 0) {
      hasZoomedOnLoad = true
      setTimeout(zoomToAllTours, 100)
    } else if (hasZoomedOnLoad && tourSetChanged) {
      setTimeout(zoomToAllTours, 100)
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => mapStyleRevision.value,
  () => {
    if (!mapLoaded.value) return

    removeLayers()
    syncMainCache()
    initializeLayers()
    refreshOverlaySource()

    if (targetResolution.value !== '500m') {
      queueMicrotask(() => {
        scheduleUpdate(updateTourSourceResolution)
      })
    }
  },
)

// Metric colorization changes the feature geometry for one tour in the main source
watch(
  () => props.metricColorizedTrack,
  () => {
    if (!mapLoaded.value) return
    syncMainCache()
    refreshMainSource()
    refreshOverlaySource()
  },
  { deep: true },
)

// Selection changes update overlay only (and filter, since edit-mode visibility depends on it)
watch(
  () => libraryStore.selectedTourIds,
  () => {
    applyMainFilter()
    refreshOverlaySource()
  },
  { deep: true },
)

// Hover: update overlay + apply global dim on main source
watch(
  () => libraryStore.hoveredTourId,
  (hoveredId) => {
    refreshOverlaySource()
    applyMainSourceDim(isRouteModeActive() || (!libraryStore.isSelectionMode && hoveredId !== null))
  },
)

// Drawing and editing both keep background tracks dim. Recompute on exit so every close/cancel
// path restores normal opacity immediately (unless a regular hover still requires dimming).
watch(
  () => [uiStore.isTrackDrawingActive, uiStore.isTrackEditMode] as const,
  ([isDrawing, isEditing]) => {
    if (isDrawing || isEditing) libraryStore.clearHoveredTour()
    refreshOverlaySource()
    applyMainSourceDim(
      isRouteModeActive() || (!libraryStore.isSelectionMode && libraryStore.hoveredTourId !== null),
    )
  },
)

// Clear hover dim immediately when selection mode is activated
watch(
  () => libraryStore.isSelectionMode,
  (active) => {
    if (active) applyMainSourceDim(isRouteModeActive())
  },
)

// Zoom to tour when name is hovered and tour is outside the current viewport
watch(
  () => libraryStore.focusedTourId,
  (focusedId) => {
    const map = mapInstance.value
    if (!map || !focusedId) return
    const bounds = tourBounds.get(focusedId)
    if (bounds) {
      optimizedFitBounds(map, bounds, {
        padding: calculateExplorePadding(),
        maxZoom: 14,
        duration: 500,
      })
    }
  },
)

// Zoom to all tours after drag-resizing the library panel
watch(
  () => uiStore.mapResizeSuppressed,
  (isSuppressed, wasSuppressed) => {
    if (!isSuppressed && wasSuppressed) {
      setTimeout(zoomToAllTours, 150)
    }
  },
)

// ── Resolution upgrades ───────────────────────────────────────────────────────

const updateTourSourceResolution = async (signal: AbortSignal) => {
  const map = mapInstance.value
  const res = targetResolution.value
  if (!map || !layersInitialized) return

  const toursToRender = getToursToRender()

  if (res === '500m') {
    let changed = false
    for (const tour of toursToRender) {
      const id = tour.clientId
      if (!id || id === 'undefined') continue
      if (renderedResolution.get(id) === '500m') continue
      const features = buildTourFeatures(tour, id)
      mainFeatureCache.set(id, features)
      if (features.length > 0) tourBounds.set(id, computeBoundsFromFeatures(features))
      renderedResolution.set(id, '500m')
      changed = true
    }
    if (changed) {
      refreshMainSource()
      refreshOverlaySource()
    }
    return
  }

  const visibleIds = Array.from(
    new Set(
      [
        ...toursToRender.map((tour) =>
          tour.clientId && tour.clientId !== 'undefined' ? tour.clientId : null,
        ),
        ...getVisibleEntityIds(LAYER_PREFIX, 'tourId'),
      ].filter((tourId): tourId is string => Boolean(tourId)),
    ),
  ).filter((tourId) => !shouldPreferLocalGeometry(tourId))

  if (visibleIds.length === 0) return

  const geometries = await fetchTourGeometries(visibleIds, res, signal)

  let changed = false
  for (const [tourId, coords] of geometries) {
    // Skip if already rendered at this resolution — avoids a no-op source swap
    // when panning/zooming over tours whose geometry hasn't changed.
    if (renderedResolution.get(tourId) === res) continue

    const sourceTour = getRenderableTourById(tourId)
    if (!sourceTour) continue
    const overrideCoordsByTrack = buildOverrideCoordsByTrack(sourceTour, coords)
    const cachedResolutions = renderedGeometryCache.get(tourId) ?? {}
    cachedResolutions[res] = overrideCoordsByTrack
    renderedGeometryCache.set(tourId, cachedResolutions)

    const features = buildTourFeatures(sourceTour, tourId, overrideCoordsByTrack)
    mainFeatureCache.set(tourId, features)
    if (features.length > 0) tourBounds.set(tourId, computeBoundsFromFeatures(features))
    renderedResolution.set(tourId, res)
    changed = true
  }

  if (changed) {
    refreshMainSource()
    refreshOverlaySource()
  }
}

const resetResolutionCaches = () => {
  clearCache()
  renderedGeometryCache.clear()
  renderedResolution.clear()
}

const handleTrackResolutionCacheInvalidated = () => {
  resetResolutionCaches()
  if (!mapLoaded.value) return

  syncMainCache()
  refreshMainSource()
  refreshOverlaySource()
  applyMainFilter()
  if (targetResolution.value !== '500m') {
    queueMicrotask(() => {
      scheduleUpdate(updateTourSourceResolution)
    })
  }
}

watch(targetResolution, () => {
  if (uiStore.areMapElementsForceHidden) return
  scheduleUpdate(updateTourSourceResolution)
})

// Re-fetch when map moves (new tours become visible at higher res)
watch(mapLoaded, (loaded) => {
  if (!loaded) return
  const map = mapInstance.value
  if (!map) return
  map.on('moveend', () => {
    if (uiStore.areMapElementsForceHidden) return
    if (targetResolution.value !== '500m') {
      scheduleUpdate(updateTourSourceResolution)
    }
  })
})

// Hold the M key (or toggle the hide button) to hide all activity tracks,
// matching WorkspaceTourLayer. Refresh sources on toggle; on un-hide, re-run a
// resolution upgrade in case the viewport changed while hidden.
let forceHideStartResolution: Resolution | null = null
watch(
  () => uiStore.areMapElementsForceHidden,
  (isHidden) => {
    if (!mapLoaded.value) return
    if (isHidden) {
      forceHideStartResolution = targetResolution.value
      refreshMainSource()
      refreshOverlaySource()
      return
    }

    const shouldRefreshResolution =
      forceHideStartResolution !== null && targetResolution.value !== forceHideStartResolution
    forceHideStartResolution = null

    refreshMainSource()
    refreshOverlaySource()
    if (shouldRefreshResolution && targetResolution.value !== '500m') {
      scheduleUpdate(updateTourSourceResolution)
    }
  },
)

// ── Window event listeners ────────────────────────────────────────────────────

const handleToursUploaded = (event: Event) => {
  const detail = (event as CustomEvent<LibraryToursUploadedDetail>).detail
  requestUploadedTourZoom(detail?.tourIds?.filter(Boolean) ?? [])
}
window.addEventListener('library-tours-uploaded', handleToursUploaded)
window.addEventListener(
  TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT,
  handleTrackResolutionCacheInvalidated,
)

const handleZoomToTour = (e: Event) => {
  const tourId = (e as CustomEvent<{ tourId: string }>).detail?.tourId
  if (!tourId) return
  const map = mapInstance.value
  if (!map) return
  const bounds = tourBounds.get(tourId)
  if (bounds && !bounds.isEmpty()) {
    optimizedFitBounds(map, bounds, {
      padding: calculateExplorePadding(),
      maxZoom: 14,
      duration: 500,
    })
  }
}
window.addEventListener('library-zoom-to-tour', handleZoomToTour)

const handleSelectionChanged = () => {
  setTimeout(zoomToAllTours, 100)
}
window.addEventListener('library-selection-changed', handleSelectionChanged)

const handleFitVisibleTours = () => {
  zoomToAllTours({ explicit: true })
  uiStore.consumeMapNavigationIntent('explicit')
}
window.addEventListener('library-fit-visible-tours', handleFitVisibleTours)

const handlePanelLayoutChanged = () => {
  setTimeout(zoomToAllTours, 160)
}
window.addEventListener('library-panel-layout-changed', handlePanelLayoutChanged)

onUnmounted(() => {
  window.removeEventListener(
    TRACK_RESOLUTION_CACHE_INVALIDATED_EVENT,
    handleTrackResolutionCacheInvalidated,
  )
  if (pendingUploadedZoomTimer) clearTimeout(pendingUploadedZoomTimer)
  hideTourPopup()
  removeLayers()
  window.removeEventListener('library-tours-uploaded', handleToursUploaded)
  window.removeEventListener('library-zoom-to-tour', handleZoomToTour)
  window.removeEventListener('library-selection-changed', handleSelectionChanged)
  window.removeEventListener('library-fit-visible-tours', handleFitVisibleTours)
  window.removeEventListener('library-panel-layout-changed', handlePanelLayoutChanged)
})
</script>

<template>
  <div></div>
</template>
